import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { url } from '../../Config';
import { useAuth } from '../../AuthContext';
import ResultCard from '../Utils/ResultCard';

const Test = () => {
  const { testName } = useParams();
  const { user } = useAuth();
  const testType = testName;

  const [test, setTest] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [intervalId, setIntervalId] = useState(null);
  const [started, setStarted] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);

  useEffect(() => {
    if (!testType) return;
    fetch(`${url}/api/test/${testType}`)
      .then(res => res.json())
      .then(data => {
        setTest(data);
        setTimeLeft((data.testTimeLimit || 5) * 60);
      });
  }, [testType]);

  useEffect(() => {
    if (!started || submitted) return;

    const id = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(id);
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    setIntervalId(id);
    return () => clearInterval(id);
  }, [started, submitted]);

  const handleSelect = (questionId, optionId) => {
    setAnswers(prev => ({ ...prev, [questionId]: optionId }));
  };

  const handleNext = () => {
    if (currentQuestion < test.questions.length - 1) {
      setCurrentQuestion(current => current + 1);
    }
  };

  const handleSubmit = async () => {
    clearInterval(intervalId);
    setSubmitted(true);
    const end = new Date();
    setEndTime(end);

    const res = await fetch(`${url}/api/submit-test/${testType}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user?.token}` },
      body: JSON.stringify({ answers }),
    });

    const resultData = await res.json();
    setResult(resultData);
  };

  const startTest = () => {
    setStarted(true);
    setStartTime(new Date());
  };

  const resetTest = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setStarted(false);
    setSubmitted(false);
    setResult(null);
    setTimeLeft((test.testTimeLimit || 5) * 60);
  };

  const formatTime = (secs) => {
    const min = Math.floor(secs / 60);
    const sec = secs % 60;
    return `${min}:${sec < 10 ? '0' : ''}${sec}`;
  };

  if (!test) return <div className="p-10 text-xl">Loading test...</div>;

 return (
  <div className="bg-gradient-to-r from-white via-[#E6F0FF] to-[#BFD7FF] w-full min-h-screen flex flex-col items-center p-8">

    {!started && !submitted && (
      <div className="bg-white/90 p-8 rounded-xl shadow-lg max-w-2xl w-full mt-6">
        <h2 className="text-3xl font-bold text-blue-800 mb-4 flex items-center justify-center gap-2">
          <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M9 12h6m-6 4h6M7 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-2" />
          </svg>
          Test Overview
        </h2>

        <p className="text-blue-900 mb-4 leading-relaxed">{test.testDescription}</p>

        <div className="bg-blue-50 border border-blue-200 p-4 rounded mb-4">
          <h3 className="text-sm font-semibold text-blue-600 mb-1">Instructions:</h3>
          <p className="text-blue-700 text-sm italic whitespace-pre-wrap">
            {test.testInstructions}
          </p>
        </div>

        <div className="text-lg font-semibold text-blue-800 mb-6">
          ⏱ Time Limit: {test.testTimeLimit} minutes
        </div>

        <div className="flex justify-center">
          <button
            onClick={startTest}
            className="px-6 py-3 text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow transition duration-300"
          >
            Start Test
          </button>
        </div>
      </div>
    )}

    {started && !submitted && (
      <>
        <div className="text-xl text-blue-800 font-semibold mb-4">
          Time left: {formatTime(timeLeft)}
        </div>

        <div className="bg-white/90 p-6 rounded-xl shadow-lg w-full max-w-xl">
          <h2 className="text-lg font-bold mb-2 text-blue-900">
            Q{currentQuestion + 1}: {test.questions[currentQuestion].text}
        {test.questions[currentQuestion].category && (
          <span className="text-sm text-blue-600">
            ({test.questions[currentQuestion].category})
          </span>
        )}

          </h2>

          {test.questions[currentQuestion].image && (
            <img
              src={`${test.questions[currentQuestion].image}`}
              alt="question"
              className="w-full h-auto mb-4 rounded"
            />
          )}

          <ul className="space-y-2">
            {test.questions[currentQuestion].options.map((opt, i) => (
              <li key={opt.id} className="flex items-center">
                <input
                  type="radio"
                  name={`question-${currentQuestion}`}
                  value={opt.id}
                  className="mr-2 accent-blue-600"
                  checked={answers[test.questions[currentQuestion].id] === opt.id}
                  onChange={() =>
                    handleSelect(test.questions[currentQuestion].id, opt.id)
                  }
                />
                {opt.image ? (
                  <img
                    src={`${opt.image}`}
                    alt={`Option ${i}`}
                    className="h-10 rounded"
                  />
                ) : (
                  <label className="text-blue-800">{opt.text}</label>
                )}
              </li>
            ))}
          </ul>

          <div className="mt-4 flex justify-end gap-2">
            {currentQuestion < test.questions.length - 1 ? (
              <button
                disabled={!answers[test.questions[currentQuestion].id]}
                onClick={handleNext}
                className="px-4 py-2 bg-blue-500 text-white rounded disabled:bg-gray-400 hover:bg-blue-600 transition"
              >
                Next
              </button>
            ) : (
            <button
              disabled={Object.keys(answers).length < test.questions.length}
              onClick={handleSubmit}
              className={`
                px-6 py-3 
                bg-gradient-to-r from-green-500 to-green-600 
                text-white font-semibold 
                rounded
                shadow-md 
                disabled:opacity-50 disabled:cursor-not-allowed
                hover:scale-103 hover:shadow-xl 
                transition-all duration-300
              `}
            >
              Submit
            </button>

            )}
          </div>
        </div>
      </>
    )}
{submitted && result && (
  <div className="mt-10 w-full justify-center items-center max-w-3xl mx-auto">
    <ResultCard
      title={test.testName}
      score={result.totalScore}
      max={result.totalMaxScore}
      grade={result.grade}
      gradeRules={result.gradeRules}
      scoresByCategory={result.scoresByCategory}
      timeUsedSec={startTime && endTime ? Math.max(0, Math.round((endTime - startTime) / 1000)) : ((test.testTimeLimit || 5) * 60 - timeLeft)}
      timeLimitSec={(test.testTimeLimit || 5) * 60}
      startTime={startTime}
      endTime={endTime}
      term={1}
      onRetake={resetTest}
      author={test.authorName}
      gradeText={result.gradeText}
      modifications={result.modifications || {}}
      createdAt={result.createdAt}
    />
  </div>
)}
  </div>
);

};

export default Test;
